// hooks/usePaginationCore.js

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAlerts, useDialog } from "../../context";
import { deleteMsg } from "../../Service/Constats";
import PaginationUI from "./components/PaginationUI";
import axios from "axios";
import useDebouncedApiCall, { formatError } from "../useDebouncedApiCall";
import { decodeParamsToValues } from "../../utils/queryParams";

const usePaginationCore = ({
  pageName,
  fetchFn,
  deleteFn,
  defaultLimit = 10,
  defaultFilter = [],
  enablePagination = true,
}) => {
  const { call } = useDebouncedApiCall();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showAlerts } = useAlerts();
  const { showError } = useDialog();

  const [data, setData] = useState([]);
  const [page, setPage] = useState(() => {
    const page = searchParams.get("page");
    if (!!page) return page;
    return 1;
  });
  const [limit] = useState(defaultLimit);
  const [totalPages, setTotalPages] = useState(0);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [filter, setFilter] = useState(() => {
    const paramData = decodeParamsToValues(searchParams, defaultFilter);
    return { ...paramData };
  });

  const cancelTokenSource = useRef(null);

  const getData = useCallback(() => {
    const rawParams = { page, limit, ...filter };

    // if (pageName === "Firmware" || pageName === "AppRelease") {
    //   delete rawParams.limit;
    //   delete rawParams.page;
    // }

    // Cancel previous request if any
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel("Cancelled due to new request");
    }
    cancelTokenSource.current = axios.CancelToken.source();

    call(() =>
      fetchFn(rawParams, { cancelToken: cancelTokenSource.current.token })
        .then((res) => {
          console.log("res", res);
          if (!res.error) {
            const total = res.data?.meta?.total || 0;
            setData(res.rowData);
            setCount(total);
            setTotalPages(Math.ceil(total / limit));
          }
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            showError(formatError(err));
          }
          setData([]);
          setCount(0);
          setTotalPages(0);
        })
        .finally(() => {
          setIsLoading(false);
        }),
    );
  });

  useEffect(() => {
    if (isLoading) {
      getData();
    }
  }, [isLoading]);

  useEffect(() => {
    setPage(1);
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel("Cancelled due to new request");
    }
    setTimeout(() => {
      setIsLoading(true);
    }, 200);
    return () => {};
  }, [filter]);

  const refresh = () => {
    setPage(1);
    setIsLoading(true);
  };

  const deleteRow = useCallback(
    (id) => {
      call(() =>
        deleteFn(id)
          .then((res) => {
            if (!res.error) {
              if (!!deleteMsg[pageName]) {
                showAlerts(deleteMsg[pageName], null, "error");
              }

              if (enablePagination) {
                setPage((prev) => (prev > 1 ? prev - 1 : 1));
                setIsLoading(true);
              } else {
                setData((prev) => prev.filter((row) => row.id !== id));
                setCount((prev) => prev - 1);
              }
            }
          })
          .catch((err) => {}),
      );
    },
    [deleteFn, pageName, enablePagination],
  );

  const updateRow = useCallback((updatedRow) => {
    setData((prev) => {
      const updates = Array.isArray(updatedRow) ? updatedRow : [updatedRow];

      const updateMap = new Map(updates.map((row) => [row.id, row]));

      return prev.map((row) =>
        updateMap.has(row.id) ? { ...row, ...updateMap.get(row.id) } : row,
      );
    });
  }, []);

  const handlePageChange = useCallback((_, value) => {
    setPage(value);
    setIsLoading(true);
  }, []);

  const shallowEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => obj1[key] === obj2[key]);
  };

  const setFilterData = useCallback((newFilter) => {
    setFilter((prev) => (shallowEqual(prev, newFilter) ? prev : newFilter));
  }, []);

  useEffect(() => {
    const paramData = decodeParamsToValues(searchParams, defaultFilter);
    setFilterData(paramData);
  }, [searchParams, defaultFilter]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", page);
    setSearchParams(params, { replace: true });
  }, [page]);

  const paginationUI = enablePagination && (
    <PaginationUI count={totalPages} page={page} onChange={handlePageChange} />
  );

  return {
    data,
    page,
    count,
    setFilter,
    setPage,
    isLoading,
    setIsLoading,
    refresh,
    deleteRow,
    updateRow,
    paginationUI,
  };
};

export default usePaginationCore;
